
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Loader2, UserIcon, ShieldCheck, Package } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { PersonalInfoTab } from "@/components/profile/PersonalInfoTab";
import { SecurityTab } from "@/components/profile/SecurityTab";
import { SubscriptionTab } from "@/components/profile/SubscriptionTab";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState("personal");
  const { user } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  const { data: userData, isLoading: isUserDataLoading, error: userDataError } = useQuery({
    queryKey: ['userData', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      console.log('🔍 Fetching user data for:', user.id);
      
      // Try to get user profile data first
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (profileError && profileError.code !== 'PGRST116') {
        console.error('❌ Profile fetch error:', profileError);
        throw profileError;
      }
      
      // Combine auth user data with profile data
      const userData = {
        id: user.id,
        email: user.email,
        name: user.user_metadata?.name || user.user_metadata?.full_name || profileData?.name || '',
        phone: user.user_metadata?.phone || profileData?.phone || '',
        ...profileData
      };
      
      console.log('✅ User data fetched successfully:', userData);
      return userData;
    },
    enabled: !!user?.id,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  if (isUserDataLoading) {
    return (
      <div className="container mx-auto py-6 flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Log for debugging
  console.log('🔧 Settings Page Debug:', {
    user: user ? { id: user.id, email: user.email } : null,
    userData,
    userDataError,
    isUserDataLoading
  });

  return (
    <div className={`mx-auto space-y-${isMobile ? "4" : "6"}`}>
      <div className="space-y-2">
        <h1 className={`${isMobile ? "text-xl" : "text-3xl"} font-bold`}>{t("profile_settings")}</h1>
        <p className={`${isMobile ? "text-sm" : ""} text-muted-foreground`}>
          {t("manage_account_settings")}
        </p>
      </div>
      
      <Tabs defaultValue="personal" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid grid-cols-3 ${isMobile ? "max-w-full mb-4" : "max-w-md mb-6"}`}>
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <UserIcon className={`${isMobile ? "h-4 w-4" : "h-4 w-4"}`} />
            <span className="hidden sm:inline">{t("personal_info")}</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <ShieldCheck className={`${isMobile ? "h-4 w-4" : "h-4 w-4"}`} />
            <span className="hidden sm:inline">{t("security")}</span>
          </TabsTrigger>
          <TabsTrigger value="subscription" className="flex items-center gap-2">
            <Package className={`${isMobile ? "h-4 w-4" : "h-4 w-4"}`} />
            <span className="hidden sm:inline">{t("subscription")}</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="personal">
          <PersonalInfoTab userData={userData} />
        </TabsContent>
        
        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>
        
        <TabsContent value="subscription">
          <SubscriptionTab userId={user?.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
